/**
 * PATH: apps/ai/src/config/tabs.ts
 * PURPOSE: AI Hub tab configuration for the shared tab system.
 */

import type { ComponentType } from 'react'
import { createTabStore, type TabNavigationConfig } from '@quotech/ui'
import {
  MessageSquare,
  Zap,
  FileText,
  BarChart3,
  History,
} from 'lucide-react'

// ─── Tab Store ──────────────────────────────────────────────
export const useAiTabStore = createTabStore({
  hubId: 'ai',
  defaultTab: { path: '/chat', title: 'Chat', iconKey: 'MessageSquare' },
})

// ─── Icon Map ───────────────────────────────────────────────
const ICON_MAP: Record<string, ComponentType<{ className?: string }>> = {
  MessageSquare,
  Zap,
  FileText,
  BarChart3,
  History,
}

// ─── Route Map ──────────────────────────────────────────────
const ROUTE_MAP: Record<string, { title: string; iconKey: string }> = {
  '/': { title: 'Chat', iconKey: 'MessageSquare' },
  '/chat': { title: 'Chat', iconKey: 'MessageSquare' },
  '/skills': { title: 'Skills', iconKey: 'Zap' },
  '/prompts': { title: 'Prompts', iconKey: 'FileText' },
  '/reports': { title: 'Reports', iconKey: 'BarChart3' },
  '/logs': { title: 'Logs', iconKey: 'History' },
}

// ─── Config ─────────────────────────────────────────────────
export const AI_TAB_CONFIG: TabNavigationConfig = {
  useStore: useAiTabStore,
  routeMap: ROUTE_MAP,
  dynamicRoutes: [
    {
      pattern: /^\/chat\/(.+)/,
      getMetadata: (path) => ({
        title: `Chat #${path.split('/')[2]}`,
        iconKey: 'MessageSquare',
      }),
    },
  ],
  excludedPaths: ['/sso', '/login', '/settings'],
  defaultNewTabPath: '/chat',
  iconMap: ICON_MAP as TabNavigationConfig['iconMap'],
}
